/**
 * Shared helpers for the spatial impact map components.
 *
 * Fuel color/name maps were duplicated in 3+ files before Phase 3 — this
 * centralizes them. Formatters are also pulled out so layer components
 * can stay thin.
 */

import type {
  DisplacedGeneratorEnriched,
  SpatialBreakdown,
  UpstreamSource,
} from "../model/domain/types";

// ──────────────────────────────────────────────────────────────────────
// Fuel visuals
// ──────────────────────────────────────────────────────────────────────

export const FUEL_COLORS: Record<string, string> = {
  coal: "#6B7280", Coal: "#6B7280", COL: "#6B7280",
  ng: "#F97316", natural_gas: "#F97316", "Natural Gas": "#F97316",
  NG: "#F97316", Gas: "#F97316",
  oil: "#92400E", Oil: "#92400E", OIL: "#92400E", Petroleum: "#92400E",
  nuclear: "#A855F7", Nuclear: "#A855F7", NUC: "#A855F7",
  hydro: "#3B82F6", Hydro: "#3B82F6", WAT: "#3B82F6",
  solar: "#FBBF24", Solar: "#FBBF24", SUN: "#FBBF24",
  wind: "#14B8A6", Wind: "#14B8A6", WND: "#14B8A6",
  other: "#D1D5DB", Other: "#D1D5DB", OTH: "#D1D5DB",
  biomass: "#84CC16", Biomass: "#84CC16", BIO: "#84CC16",
  geothermal: "#14B8A6", Geothermal: "#14B8A6", GEO: "#14B8A6",
};

export function getFuelColor(fuel: string): string {
  if (FUEL_COLORS[fuel]) return FUEL_COLORS[fuel];
  const key = Object.keys(FUEL_COLORS).find(
    (k) => fuel.toLowerCase().includes(k.toLowerCase()),
  );
  return key ? FUEL_COLORS[key] : "#9CA3AF";
}

export function getFuelDisplayName(fuel: string): string {
  const map: Record<string, string> = {
    COL: "Coal", coal: "Coal", Coal: "Coal",
    NG: "Natural Gas", ng: "Natural Gas", natural_gas: "Natural Gas",
    "Natural Gas": "Natural Gas", Gas: "Gas", gas: "Gas",
    OIL: "Oil", oil: "Oil", Oil: "Oil", Petroleum: "Petroleum",
    NUC: "Nuclear", nuclear: "Nuclear", Nuclear: "Nuclear",
    WAT: "Hydro", hydro: "Hydro", Hydro: "Hydro",
    SUN: "Solar", solar: "Solar", Solar: "Solar",
    WND: "Wind", wind: "Wind", Wind: "Wind",
    OTH: "Other", other: "Other", Other: "Other",
    BIO: "Biomass", biomass: "Biomass", Biomass: "Biomass",
    GEO: "Geothermal", geothermal: "Geothermal", Geothermal: "Geothermal",
  };
  return map[fuel] ?? fuel.replace(/_/g, " ");
}

// ──────────────────────────────────────────────────────────────────────
// Formatters
// ──────────────────────────────────────────────────────────────────────

export function fmtGwh(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 100) return v.toFixed(0);
  if (abs >= 10) return v.toFixed(1);
  if (abs >= 1) return v.toFixed(2);
  if (abs >= 0.01) return v.toFixed(3);
  return v.toExponential(1);
}

export function fmtMw(v: number): string {
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  if (v >= 100) return v.toFixed(0);
  if (v >= 10) return v.toFixed(1);
  return v.toFixed(2);
}

export function fmtUsd(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
}

export function fmtNum(v: number, digits = 2): string {
  const abs = Math.abs(v);
  if (abs >= 1000) return v.toFixed(0);
  if (abs >= 10) return v.toFixed(1);
  if (abs >= 0.01) return v.toFixed(digits);
  if (abs === 0) return "0";
  return v.toExponential(1);
}

// ──────────────────────────────────────────────────────────────────────
// Metric plumbing
// ──────────────────────────────────────────────────────────────────────

export type MetricKey = "DALYs" | "PDFs" | "USD";
export type ScaleKey = "global" | "regional" | "local";

export const METRIC_LABEL: Record<MetricKey, string> = {
  DALYs: "DALYs",
  PDFs: "Species-yr",
  USD: "USD",
};

export function fmtMetric(value: number, metric: MetricKey): string {
  if (metric === "USD") return fmtUsd(value);
  if (metric === "PDFs") {
    const abs = Math.abs(value);
    if (abs === 0) return "0";
    if (abs < 0.001) return value.toExponential(1);
    if (abs < 1) return value.toFixed(4);
    return value.toFixed(2);
  }
  // DALYs
  const abs = Math.abs(value);
  if (abs === 0) return "0";
  if (abs >= 100) return value.toFixed(0);
  if (abs >= 10) return value.toFixed(1);
  if (abs >= 0.01) return value.toFixed(2);
  return value.toExponential(1);
}

/** Pull the configured metric for a scale from a plant's spatial_breakdown. */
export function pickMetric(
  plant: DisplacedGeneratorEnriched,
  scale: ScaleKey,
  metric: MetricKey,
): number {
  const b: SpatialBreakdown | undefined = plant.spatial_breakdown?.[scale];
  if (!b) return 0;
  return Math.abs(b[metric] ?? 0);
}

/** Sum a given scale+metric across all plants. */
export function totalByScale(
  plants: DisplacedGeneratorEnriched[],
  scale: ScaleKey,
  metric: MetricKey,
): number {
  return plants.reduce((s, p) => s + pickMetric(p, scale, metric), 0);
}

// ──────────────────────────────────────────────────────────────────────
// Category breakdown helpers
// ──────────────────────────────────────────────────────────────────────

/**
 * Pretty-print an impacts_by_category key like
 *   "particulate matter formation_DALYs" → "Particulate Matter"
 */
export function prettyCategoryName(key: string): string {
  // Strip unit suffix
  const cleaned = key.split("_")[0];
  return cleaned
    .split(" ")
    .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : w))
    .join(" ")
    .replace(/:/g, ":");
}

/**
 * Metric-specific suffix used in `impacts_by_category` keys.
 * DALYs → "_DALYs"; PDFs (species·yr) → "_species.yr"; USD → "_USD 2013".
 */
export function suffixForMetric(metric: MetricKey): string {
  if (metric === "PDFs") return "_species.yr";
  if (metric === "USD") return "_USD 2013";
  return "_DALYs";
}

/**
 * Top N impact categories for a plant, filtered to the current metric.
 * Previously this mixed DALYs + species·yr + USD rows into a single list
 * (illegible to customers). Now it returns only rows whose unit matches
 * the selected metric so all values are in the same unit.
 */
export function topImpactCategories(
  impacts: Record<string, number> | undefined,
  n = 4,
  metric: MetricKey = "DALYs",
): Array<{ key: string; label: string; value: number }> {
  if (!impacts) return [];
  const suffix = suffixForMetric(metric);
  return Object.entries(impacts)
    .filter(([k]) => k.endsWith(suffix))
    .map(([k, v]) => ({ key: k, label: prettyCategoryName(k), value: Math.abs(v) }))
    .sort((a, b) => b.value - a.value)
    .slice(0, n);
}

/**
 * Dominant regional pollutants from emissions_by_category.
 * Returns pollutant labels weighted toward regional-scale categories
 * (PM, ozone, acidification, toxicity).
 */
const REGIONAL_POLLUTANT_KEYS: Array<{ match: RegExp; label: string }> = [
  { match: /particulate matter/i, label: "PM" },
  { match: /photochemical|ozone formation|smog/i, label: "photochemical ozone" },
  { match: /human toxicity|toxicity: human/i, label: "toxicity" },
  { match: /acidification/i, label: "acidification" },
  { match: /eutrophication/i, label: "eutrophication" },
  { match: /ecotoxicity/i, label: "ecotoxicity" },
];

export function dominantRegionalPollutants(
  impacts: Record<string, number> | undefined,
  maxN = 4,
): Array<{ label: string; share: number }> {
  if (!impacts) return [];
  const buckets = REGIONAL_POLLUTANT_KEYS.map(({ match, label }) => {
    const sum = Object.entries(impacts)
      .filter(([k]) => match.test(k))
      .reduce((s, [, v]) => s + Math.abs(v), 0);
    return { label, value: sum };
  }).filter((b) => b.value > 0);
  const total = buckets.reduce((s, b) => s + b.value, 0);
  if (total <= 0) return [];
  return buckets
    .sort((a, b) => b.value - a.value)
    .slice(0, maxN)
    .map(({ label, value }) => ({ label, share: value / total }));
}

// ──────────────────────────────────────────────────────────────────────
// Upstream source display
// ──────────────────────────────────────────────────────────────────────

/** Named mine/basin vs. fallback (affects pin styling). */
export function isNamedSource(src: UpstreamSource | undefined): boolean {
  return src === "eia923_msha" || src === "eia923_gas_state";
}

export function upstreamSourceLabel(src: UpstreamSource | undefined): string {
  switch (src) {
    case "eia923_msha":
      return "MSHA-matched mine";
    case "eia923_gas_state":
      return "State-weighted basin";
    case "eia923_unresolved_msha_national_fallback":
      return "National fallback (MSHA miss)";
    case "eia923_unknown_state_national_fallback":
      return "National fallback (unknown state)";
    case "national_average":
    default:
      return "National average";
  }
}

// ──────────────────────────────────────────────────────────────────────
// Headline translators — the three ReCiPe endpoints (Health / Bio / Resource)
//
// NOTE: the previous version converted CO2-eq → "cars off the road".
// That framing is carbon-only and misrepresents a full-LCA product.
// Replaced with the three-endpoint framing customers actually buy.
// ──────────────────────────────────────────────────────────────────────

export function fmtTonnes(kg: number): string {
  const tonnes = Math.abs(kg) / 1000;
  if (tonnes >= 1_000_000) return `${(tonnes / 1_000_000).toFixed(1)}M tonnes`;
  if (tonnes >= 1_000) return `${(tonnes / 1_000).toFixed(1)}k tonnes`;
  if (tonnes >= 1) return `${tonnes.toFixed(0)} tonnes`;
  return `${(Math.abs(kg)).toFixed(0)} kg`;
}

export function totalCo2eKg(plants: DisplacedGeneratorEnriched[]): number {
  return plants.reduce(
    (s, p) => s + Math.abs(p.emissions_by_category?.["climate change_kg CO2-Eq"] ?? 0),
    0,
  );
}

/** Three-endpoint totals summed across all plants, in absolute-value terms. */
export interface EndpointTotals {
  dalys: number;   // years of healthy life
  pdfs: number;    // species·yr
  usd: number;     // USD 2013
}

export function totalEndpoints(
  plants: DisplacedGeneratorEnriched[],
): EndpointTotals {
  return plants.reduce<EndpointTotals>(
    (acc, p) => ({
      dalys: acc.dalys + Math.abs(p.dalys ?? 0),
      pdfs: acc.pdfs + Math.abs(p.pdfs ?? 0),
      usd: acc.usd + Math.abs(p.usd ?? 0),
    }),
    { dalys: 0, pdfs: 0, usd: 0 },
  );
}

/** Sum per-scale endpoints across all plants, using `spatial_breakdown`. */
export function totalEndpointsByScale(
  plants: DisplacedGeneratorEnriched[],
  scale: ScaleKey,
): EndpointTotals {
  return plants.reduce<EndpointTotals>(
    (acc, p) => {
      const b = p.spatial_breakdown?.[scale];
      return {
        dalys: acc.dalys + Math.abs(b?.DALYs ?? 0),
        pdfs: acc.pdfs + Math.abs(b?.PDFs ?? 0),
        usd: acc.usd + Math.abs(b?.USD ?? 0),
      };
    },
    { dalys: 0, pdfs: 0, usd: 0 },
  );
}

// ──────────────────────────────────────────────────────────────────────
// Exposed-population estimates
// ──────────────────────────────────────────────────────────────────────

/**
 * 2020 Census state populations (50 states + DC), in millions.
 * Hard-coded because the alternative — a Census API call — is overkill
 * for a v1 "is this airshed big?" sanity check.
 */
export const STATE_POPULATION_MILLIONS: Record<string, number> = {
  AL: 5.0, AK: 0.73, AZ: 7.2, AR: 3.0, CA: 39.5, CO: 5.8, CT: 3.6,
  DE: 1.0, DC: 0.68, FL: 21.5, GA: 10.7, HI: 1.46, ID: 1.84, IL: 12.8,
  IN: 6.8, IA: 3.2, KS: 2.94, KY: 4.5, LA: 4.66, ME: 1.36, MD: 6.18,
  MA: 7.0, MI: 10.1, MN: 5.7, MS: 2.96, MO: 6.15, MT: 1.08, NE: 1.96,
  NV: 3.1, NH: 1.38, NJ: 9.29, NM: 2.12, NY: 20.2, NC: 10.4, ND: 0.78,
  OH: 11.8, OK: 3.96, OR: 4.24, PA: 13.0, RI: 1.1, SC: 5.12, SD: 0.89,
  TN: 6.9, TX: 29.1, UT: 3.27, VT: 0.64, VA: 8.63, WA: 7.72, WV: 1.79,
  WI: 5.89, WY: 0.58,
};

export function stateName(code: string): string {
  const map: Record<string, string> = {
    AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas",
    CA: "California", CO: "Colorado", CT: "Connecticut", DE: "Delaware",
    DC: "D.C.", FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho",
    IL: "Illinois", IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky",
    LA: "Louisiana", ME: "Maine", MD: "Maryland", MA: "Massachusetts",
    MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
    MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire",
    NJ: "New Jersey", NM: "New Mexico", NY: "New York", NC: "North Carolina",
    ND: "North Dakota", OH: "Ohio", OK: "Oklahoma", OR: "Oregon",
    PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
    SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah",
    VT: "Vermont", VA: "Virginia", WA: "Washington", WV: "West Virginia",
    WI: "Wisconsin", WY: "Wyoming",
  };
  return map[code] ?? code;
}

/**
 * Format a population count in millions into an "M people" string.
 * Very rough; fine for a "how big is this airshed?" tooltip.
 */
export function fmtPopulation(millions: number): string {
  if (millions >= 100) return `~${millions.toFixed(0)}M people`;
  if (millions >= 10) return `~${millions.toFixed(0)}M people`;
  if (millions >= 1) return `~${millions.toFixed(1)}M people`;
  if (millions >= 0.1) return `~${(millions * 1000).toFixed(0)}k people`;
  return "<100k people";
}

/**
 * Very rough exposed-population estimate for an airshed, by taking the
 * host state's population and scaling up ~1.8× to approximate spillover
 * into adjacent states (airsheds routinely cross 2-5 state borders).
 *
 * This is a v1 "order-of-magnitude" estimate — good enough to let a
 * customer gut-check "is this a lot?". A real geodesic intersect against
 * Census tracts can come later.
 */
export function approxAirshedPopulation(state: string | undefined): {
  millions: number;
  nStates: number;
} {
  if (!state) return { millions: 0, nStates: 0 };
  const host = STATE_POPULATION_MILLIONS[state.toUpperCase()] ?? 0;
  // Airsheds at ~500 km typically touch ~3-5 states. Use a 2.5× multiplier
  // to rough that in. Caps at US total.
  const millions = Math.min(host * 2.5, 334);
  return { millions, nStates: Math.max(1, Math.round(millions / 6)) };
}

/**
 * 50-km-ring exposed population: just use ~10% of host state population as
 * an indicative number, since metro-area populations at that scale vary
 * wildly (1M-20M). A rough lower bound.
 */
export function approxLocalPopulation(state: string | undefined): number {
  if (!state) return 0;
  const host = STATE_POPULATION_MILLIONS[state.toUpperCase()] ?? 0;
  return host * 0.15;
}

// ──────────────────────────────────────────────────────────────────────
// Three-endpoint number formatters — shared across the tab headlines
// ──────────────────────────────────────────────────────────────────────

/** "1.23 years" / "450 years" / "2.3M years" — DALYs formatted as years. */
export function fmtYearsHealth(dalys: number): string {
  const v = Math.abs(dalys);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M years`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k years`;
  if (v >= 100) return `${v.toFixed(0)} years`;
  if (v >= 10) return `${v.toFixed(1)} years`;
  if (v >= 1) return `${v.toFixed(2)} years`;
  if (v > 0) return `${v.toFixed(3)} years`;
  return "0 years";
}

/** "1.23 species·yr" — PDFs formatted for a lay reader. */
export function fmtSpeciesYr(pdfs: number): string {
  const v = Math.abs(pdfs);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M species·yr`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k species·yr`;
  if (v >= 100) return `${v.toFixed(0)} species·yr`;
  if (v >= 10) return `${v.toFixed(1)} species·yr`;
  if (v >= 1) return `${v.toFixed(2)} species·yr`;
  if (v > 0) return `${v.toFixed(3)} species·yr`;
  return "0 species·yr";
}
